import { ConfirmDeleteModal, CustomSelect } from "@components/UI";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { get_policy_attributes, getPolicyStepColors, getPolicyApprovalOptions } from "../../constants/licensePolicy";
import { CustomInput } from "../Forms";
import { ChevronUpIcon, PolicyDocumentsIcon, TrashIcon } from "../Icons";
import { AttributeItem, ColorBox } from "./index";
import { CHARACTER_LENGTH } from "@constants/index";
import { useTheme } from "@hooks/useTheme";

// Helper function to sort attributes by order
const sortAttributesByOrder = (attributes, policyAttributes) => {
  const sorted = attributes.sort((a, b) => {
    // Handle both existing items (licensePolicyCd) and new items (code)
    const aCode = a.licensePolicyCd || a.code;
    const bCode = b.licensePolicyCd || b.code;

    // For existing items, find the order from constants using the code
    // For new items, use the order field directly
    const aOrder = a.order || policyAttributes.find((attr) => attr.code === aCode)?.order || 999;
    const bOrder = b.order || policyAttributes.find((attr) => attr.code === bCode)?.order || 999;

    return aOrder - bOrder;
  });

  return sorted;
};

const PolicyStepForm = ({
  stepNumber,
  stepData,
  index,
  onStepChange,
  onDeleteStep,
  onStepExpand,
  isExpanded,
  colors,
  policySteps,
  colorErrors,
  stepNumberErrors,
}) => {
  const { t } = useTranslation();
  const policyAttributes = useMemo(() => get_policy_attributes(t), [t]);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(".step-dropdown-container")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Create step options for dropdown - constant 5 steps
  const stepOptions = [
    { value: 1, label: `${t("license-policy.step")} 1`, stepNumber: 1 },
    { value: 2, label: `${t("license-policy.step")} 2`, stepNumber: 2 },
    { value: 3, label: `${t("license-policy.step")} 3`, stepNumber: 3 },
    { value: 4, label: `${t("license-policy.step")} 4`, stepNumber: 4 },
    { value: 5, label: `${t("license-policy.step")} 5`, stepNumber: 5 },
  ];

  const handleDeleteStep = () => {
    setShowModalDelete(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await onDeleteStep(index);
      setShowModalDelete(false);
    } catch (error) {
      console.error("Error deleting step:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e) => {
    e.preventDefault();

    try {
      const attributeData = e.dataTransfer.getData("application/json");

      if (attributeData) {
        const attribute = JSON.parse(attributeData);

        // Check if attribute is already in this step
        if (!stepData.licensePolicyStepItems.find((attr) => attr.code === attribute.code)) {
          const newAttributes = [...stepData.licensePolicyStepItems, attribute];
          // Sort attributes by their order field
          const sortedAttributes = sortAttributesByOrder(newAttributes, policyAttributes);
          onStepChange(index, { ...stepData, licensePolicyStepItems: sortedAttributes });
        } else {
          // Attribute already exists
        }
      }
    } catch (error) {
      console.error("Error parsing dropped attribute:", error);
    }
  };

  const removeAttribute = (attributeCode) => {
    const newAttributes = stepData.licensePolicyStepItems.filter((attr) => attr.code !== attributeCode);
    // Sort attributes by their order field after removal
    const sortedAttributes = sortAttributesByOrder(newAttributes, policyAttributes);
    onStepChange(index, { ...stepData, licensePolicyStepItems: sortedAttributes });
  };

  return (
    <div className={`${colors.bgPolicyStepItem} rounded-lg border ${colors.borderPrimary} mb-4`} data-testid={`license-policy-step-${index}`}>
      {/* Step Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        data-testid={`license-policy-step-${index}-header-toggle`}
        onClick={() => onStepExpand(index)}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid={`license-policy-step-${index}-expand-button`}
            onClick={() => onStepExpand(index)}
            className={`${colors.textPrimary} ${colors.textHoverActive} transition-colors`}
          >
            <ChevronUpIcon
              size={20}
              className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
            />
          </button>

          {/* Step Dropdown Select */}
          <div className="relative step-dropdown-container">
            <button
              type="button"
              data-testid={`license-policy-step-${index}-dropdown-toggle`}
              onClick={(e) => {
                e.stopPropagation();
                setIsDropdownOpen(!isDropdownOpen);
              }}
              className={`inline-flex items-center justify-center text-sm font-medium ${colors.textActive} ${colors.textHoverActive} transition-colors cursor-pointer`}
            >
              <span className="font-medium">
                {t("license-policy.step")} {stepNumber}
              </span>
              <svg
                className={`ml-2 h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div 
                className="absolute left-0 z-[9999] mt-1 w-48 rounded-[0.25rem] border-t-0 p-[0.125rem]"
                style={{
                  backgroundColor: colors.bgPrimaryColor,
                  boxShadow: "0px 0px 6px 1px rgba(0, 0, 0, 0.6)"
                }}
              >
                <div 
                  className="rounded-[0.5rem] p-2 m-0"
                  style={{
                    backgroundColor: colors.bgPrimaryColor,
                    color: colors.textPrimaryColor
                  }}
                >
                  {stepOptions.map((option, optionIndex) => {
                    const isSelected = option.value === stepNumber;
                    const isHovered = hoveredOption === option.value;
                    const isActive = isSelected || isHovered;
                    const isLast = optionIndex === stepOptions.length - 1;
                    
                    return (
                      <button
                        key={option.value}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (option.value !== stepNumber) {
                            // Change the current step number to the selected step
                            const updatedStepData = {
                              ...stepData,
                              stepNumber: option.value,
                            };
                            // Call onStepChange with the object index, not the step number
                            onStepChange(index, updatedStepData);
                          }
                          setIsDropdownOpen(false);
                        }}
                        onMouseEnter={() => setHoveredOption(option.value)}
                        onMouseLeave={() => setHoveredOption(null)}
                        className={`flex w-full items-center text-left text-sm transition-all duration-200 relative rounded-[0.25rem] px-3 py-2 ${
                          !isLast ? 'mb-[3px]' : ''
                        }`}
                        style={{
                          backgroundColor: colors.bgPrimaryColor,
                          color: isActive ? colors.textActiveColor : colors.textPrimaryColor,
                          border: isActive ? `1px solid ${colors.dropdownBorderColor}` : "1px solid transparent",
                          boxShadow: isActive ? "0px -0.1px 4px 1px rgba(0, 0, 0, 0.6)" : "none",
                          zIndex: isActive ? 1000 : 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        <span className="font-medium">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {stepNumberErrors?.[index] && (
            <p className={`${colors.textDanger} text-sm mt-1`}>{t(stepNumberErrors[index])}</p>
          )}
        </div>
        <button
          type="button"
          data-testid={`license-policy-step-${index}-delete-button`}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteStep();
          }}
          className={`transition-colors text-red-500 hover:text-red-400 cursor-pointer`}
        >
          <TrashIcon size={16} />
        </button>
      </div>

      {/* Form Fields - Always Visible */}
      <div className="px-6 pb-4">
        <div className="flex gap-6 mb-6">
          {/* Policy Step Color */}
          <div className="w-36">
            <label className={`block text-sm ${colors.textSubTitle} font-medium mb-2`}>
              {t("license-policy.obligation-type-color")}
            </label>
            <CustomSelect
              options={getPolicyStepColors(t)}
              value={getPolicyStepColors(t).find((color) => color.value === stepData.color)}
              onChange={(selected) => onStepChange(index, { ...stepData, color: selected.value })}
              placeholder="Select color"
              clearable={false}
              formatOptionLabel={(option) => (
                <div className="flex items-center gap-2">
                  <ColorBox colorName={option.value} size="md" showLabel={false} />
                  <span>{option.label}</span>
                </div>
              )}
            />
            {colorErrors?.[index] && (
              <p className={`${colors.textDanger} text-sm mt-1`}>{t(colorErrors[index])}</p>
            )}
          </div>

          {/* License Use Approval */}
          <div className="w-46">
            <label className={`block text-sm ${colors.textSubTitle} font-medium mb-2`}>
              {t("license-policy.license-use-approval")}
            </label>
            <CustomSelect
              options={getPolicyApprovalOptions(t)}
              value={getPolicyApprovalOptions(t).find((option) => option.value === stepData.approval)}
              onChange={(selected) => onStepChange(index, { ...stepData, approval: selected.value })}
              placeholder="Select approval type"
              clearable={false}
            />
          </div>

          {/* Remark */}
          <div className="flex-1">
            <label className={`block text-sm ${colors.textSubTitle} font-medium mb-2`}>{t("license-policy.remark")}</label>
            <CustomInput
              value={stepData.remark}
              onChange={(e) => onStepChange(index, { ...stepData, remark: e.target.value })}
              placeholder={t("license-policy.write-remark")}
              size="md"
              maxLength={CHARACTER_LENGTH.COMMENT}
            />
          </div>
        </div>

        {/* Item List - Only when expanded */}
        {isExpanded && (
          <div>
            <label className={`block text-sm ${colors.textSubTitle} font-medium mb-2`}>
              {t("license-policy.license-policy-item-list")}
            </label>

            {/* Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnter={(e) => {
                e.preventDefault();
              }}
              className={`border-2 border-dashed ${colors.borderSelectedOption} rounded-lg p-8 text-center ${colors.bgSelectedOption} mb-2`}
            >
              <div className="mb-2 flex justify-center">
                <PolicyDocumentsIcon size={32} />
              </div>
              <div className={`${colors.textActive} text-sm font-medium mb-1`}>{t("license-policy.please-drop")}</div>
              <div className={`${colors.textSubTitle} text-xs`}>{t("license-policy.please-drag-and-drop")}</div>
            </div>

            {/* Attributes List */}
            {stepData.licensePolicyStepItems.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {sortAttributesByOrder([...stepData.licensePolicyStepItems], policyAttributes).map(
                  (attribute, index) => (
                    <AttributeItem
                      key={`${attribute.code}-${index}`}
                      attribute={attribute}
                      showDelete={true}
                      onDelete={(attr) => removeAttribute(attr.code)}
                      isInStep={true}
                    />
                  ),
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={showModalDelete}
        onClose={() => setShowModalDelete(false)}
        onConfirm={handleConfirmDelete}
        title={t("license-policy.confirm-delete-step-title")}
        description={t("license-policy.confirm-delete-step-description")}
        cancelText={t("common.cancel-button")}
        confirmText={t("common.delete-button")}
        loading={deleteLoading}
      />
    </div>
  );
};

const LicensePolicySteps = ({
  policySteps,
  expandedStep,
  onStepChange,
  onDeleteStep,
  onStepExpand,
  addNewStep,
  colors,
  colorErrors,
  stepNumberErrors,
}) => {
  const { t } = useTranslation();
  const policyAttributes = useMemo(() => {
    return get_policy_attributes(t);
  }, [t]);

  return (
    <div className={`${colors.bgPrimary} w-full mt-4 rounded-lg p-6 border ${colors.borderPrimary}`} data-testid="license-policy-steps">
      <h1 className={`text-xl font-semibold ${colors.textNormal} mb-6 uppercase`}>
        {t("license-policy.license-policy-item-list")}
      </h1>

      <div className="flex gap-4 w-full">
        {/* Left Side - Steps Card */}
        <div className={`w-[60%] max-h-[100vh] overflow-y-auto`}>
          {Object.entries(policySteps).map(([index, stepData]) => (
            <PolicyStepForm
              key={index}
              index={Number(index)}
              stepNumber={stepData.stepNumber}
              stepData={stepData}
              isExpanded={expandedStep === Number(index)}
              onStepChange={onStepChange}
              onDeleteStep={onDeleteStep}
              onStepExpand={onStepExpand}
              colors={colors}
              policySteps={policySteps}
              colorErrors={colorErrors}
              stepNumberErrors={stepNumberErrors}
            />
          ))}

          {Object.keys(policySteps).length < 5 && (
            <button
              type="button"
              data-testid="license-policy-steps-add-step-button"
              onClick={addNewStep}
              disabled={Object.keys(policySteps).length >= 5}
              className={`w-full inline-flex items-center justify-center px-6 py-2 border-2 border-dashed ${colors.borderOutLineButton} rounded-lg text-sm font-semibold ${colors.textActive} ${colors.bgAddVerificationFileBtn} transition-all duration-200 hover:opacity-80`}
            >
              + {t("license-policy.add-step-button")}
            </button>
          )}
        </div>

        {/* Right Side - Attributes List Card */}
        <div
          className={`w-[40%] max-h-[100vh] overflow-y-auto ${colors.bgPolicyStepItem} rounded-lg p-3 border ${colors.borderPrimary}`}
        >
          <h3 className={`${colors.textNormal} font-medium mb-1`}>{t("license-policy.attributes-list")}</h3>

          {/* Get selected attributes only from the currently expanded step */}
          {(() => {
            const currentStepData = policySteps[expandedStep];
            const selectedAttributes = currentStepData ? currentStepData.licensePolicyStepItems : [];
            const availableAttributes = policyAttributes.filter(
              (attr) => !selectedAttributes.find((selected) => selected.code === attr.code),
            );

            return availableAttributes.length > 0 ? (
              <>
                {/* <p className="text-[#9EA5B3] text-sm mb-1">Right to distribute object/binary code</p> */}
                <div className="space-y-2">
                  {availableAttributes.map((attribute) => (
                    <AttributeItem
                      key={attribute.code}
                      attribute={attribute}
                      onDragStart={(e) => {
                        e.dataTransfer.setData("application/json", JSON.stringify(attribute));
                        e.dataTransfer.effectAllowed = "copy";
                      }}
                      isInStep={false}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <div className={`${colors.textSubTitle} text-4xl mb-2`}>✓</div>
                <div className={`${colors.textSubTitle} text-sm`}>{t("license-policy.all-attributes-are-set")}</div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default LicensePolicySteps;
